import type { ResumeSectionType } from '@applicator/shared'

import { RESUME_SECTIONS } from '../components/ResumeDetail/constants'

export type SectionEmptyHintVariant = 'section-plus' | 'inline-button'

export interface SectionEmptyHintConfig {
  variant: SectionEmptyHintVariant
  /** Lowercase phrase after "No … yet" (e.g. "projects"). */
  itemsPhrase: string
  /** Shown after "Click" for inline-button sections (e.g. "+ Add Group below"). */
  inlineAction?: string
}

const SECTION_EMPTY_HINT_CONFIG: Partial<Record<ResumeSectionType, SectionEmptyHintConfig>> = {
  work_experience: { variant: 'section-plus', itemsPhrase: 'work experience' },
  education: { variant: 'section-plus', itemsPhrase: 'education' },
  skills: {
    variant: 'inline-button',
    itemsPhrase: 'skill groups',
    inlineAction: '+ Add Group below',
  },
  interests: { variant: 'section-plus', itemsPhrase: 'interests' },
  certifications: { variant: 'section-plus', itemsPhrase: 'certifications' },
  awards_scholarships: { variant: 'section-plus', itemsPhrase: 'awards or scholarships' },
  projects: { variant: 'section-plus', itemsPhrase: 'projects' },
  professional_summary: { variant: 'section-plus', itemsPhrase: 'summary entries' },
  target_title: { variant: 'section-plus', itemsPhrase: 'target titles' },
  volunteering_leadership: { variant: 'section-plus', itemsPhrase: 'volunteering or leadership entries' },
  publications: { variant: 'section-plus', itemsPhrase: 'publications' },
}

export function getResumeSectionLabel(sectionType: ResumeSectionType): string {
  return RESUME_SECTIONS.find((section) => section.type === sectionType)?.label ?? sectionType
}

export function getResumeSectionEmptyHintConfig(
  sectionType: ResumeSectionType
): SectionEmptyHintConfig | null {
  return SECTION_EMPTY_HINT_CONFIG[sectionType] ?? null
}

export function getResumeSectionEmptyHintMessage(sectionType: ResumeSectionType): string | null {
  const config = getResumeSectionEmptyHintConfig(sectionType)
  if (!config) return null

  if (config.variant === 'section-plus') {
    const sectionLabel = getResumeSectionLabel(sectionType)
    return `No ${config.itemsPhrase} yet. Use + to add ${sectionLabel}.`
  }

  return `No ${config.itemsPhrase} yet. Click ${config.inlineAction}.`
}

/** True when section content is an empty list (or missing). */
export function isResumeSectionListEmpty(data: unknown): boolean {
  return !Array.isArray(data) || data.length === 0
}
